/**
 * Falls back to a <picture>'s own <img src> when the <source> the browser picked fails to load.
 * Browsers do not try the next candidate on error, so a dead theme variant (for example a
 * `prefers-color-scheme: dark` source that 404s) otherwise renders as a broken image.
 */
function fallBackToImgSrc(img: HTMLImageElement) {
  const picture = img.parentElement;
  if (!(picture instanceof HTMLPictureElement) || !img.getAttribute('src')) {
    return;
  }

  picture.querySelectorAll(':scope > source').forEach((source) => source.remove());
}

function handleImageError(event: Event) {
  if (event.target instanceof HTMLImageElement) {
    fallBackToImgSrc(event.target);
  }
}

/**
 * Repairs <picture> images inside `container` that already failed, and those that fail later.
 */
export function installPictureSourceFallback(container: HTMLElement): () => void {
  // Server-rendered images can fail before hydration attaches the error listener.
  container.querySelectorAll<HTMLImageElement>('picture > img').forEach((img) => {
    if (img.complete && img.naturalWidth === 0 && img.currentSrc && img.currentSrc !== img.src) {
      fallBackToImgSrc(img);
    }
  });

  // error does not bubble, so listen in the capture phase.
  container.addEventListener('error', handleImageError, true);
  return () => container.removeEventListener('error', handleImageError, true);
}
